import type {
  OrganizationCreate,
  OrganizationResponse,
  OrganizationUpdate,
} from '@/api/generated';
import { isFieldDirty, type DirtyFieldMap } from '@/lib/dirtyFields';
import {
  contactPart,
  contactToPayload,
  emailField,
  optionalText,
  scopeNeedsStates,
  websiteField,
} from '@/lib/organization';
import {
  socialLinksSchema,
  toSocialLinkRows,
  toSocialLinksPayload,
} from '@/lib/socialLinks';
import { z } from 'zod';

/**
 * The staff form's own rules. What it shares with the Super Admin's profile
 * form - the option lists, the state codes, the field rules - lives in
 * `@/lib/organization`; what is here is what only staff ever see.
 */

/**
 * Only the create form offers a starting status, so this stays with the staff
 * form rather than moving to the shared module.
 */
export const STARTING_STATUSES = [
  { value: 'PENDING', label: 'Pending — created but not yet live' },
  { value: 'ACTIVE', label: 'Active — visible in the app straight away' },
] as const;

/** The four form fields that together make up the primary contact. */
const CONTACT_FIELDS = [
  'contact_name',
  'contact_title',
  'contact_email',
  'contact_phone',
] as const;

export const organizationFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, 'Enter the organization name')
      .max(200, 'Use 200 characters or fewer'),

    slug: z
      .string()
      .trim()
      .optional()
      .transform((value) => (value === '' ? undefined : value))
      .refine(
        (value) => value === undefined || /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value),
        'Use lower case letters, numbers and single hyphens',
      ),

    organization_type: z.string().optional(),
    description: optionalText(),

    website: websiteField(),

    geographic_scope: z.string().optional(),
    geographic_states: z.array(z.string()).default([]),
    scope_description: optionalText(),

    contact_name: optionalText(),
    contact_title: optionalText(),
    contact_email: emailField(),
    contact_phone: optionalText(),

    social_links: socialLinksSchema.default([]),

    status: z.string().default('PENDING'),
  })
  .refine(
    (values) =>
      !scopeNeedsStates(values.geographic_scope) || values.geographic_states.length > 0,
    {
      // A scope of "state" with no state named is not a scope at all.
      message: 'Choose at least one state for this scope',
      path: ['geographic_states'],
    },
  );

/**
 * The same rules with one addition: an account that already exists must keep a
 * web handle.
 *
 * On the create form a blank handle means "build one from the name", which the
 * backend does. There is nothing to build from on an existing account, and the
 * backend refuses to clear a slug, so it is caught here - before a save that
 * could only fail.
 */
export const organizationEditFormSchema = organizationFormSchema.refine(
  (values) => Boolean(values.slug),
  {
    message: 'An account cannot be without a web handle',
    path: ['slug'],
  },
);

export type OrganizationFormValues = z.input<typeof organizationFormSchema>;
export type OrganizationFormOutput = z.output<typeof organizationFormSchema>;

/** The details form's own dirty map. The rule itself lives in `lib`. */
export type OrganizationDirtyFields = DirtyFieldMap<keyof OrganizationFormValues>;

export const EMPTY_ORGANIZATION: OrganizationFormValues = {
  name: '',
  slug: '',
  organization_type: '',
  description: '',
  website: '',
  geographic_scope: '',
  geographic_states: [],
  scope_description: '',
  contact_name: '',
  contact_title: '',
  contact_email: '',
  contact_phone: '',
  social_links: [],
  status: 'PENDING',
};

/**
 * Turn a stored account into the flat shape the form holds.
 *
 * Every absent value becomes an empty string rather than staying null, because
 * a React input given null is an uncontrolled input, and the first keystroke in
 * it produces a warning and loses the form's record of what changed.
 */
export function toFormValues(record: OrganizationResponse): OrganizationFormValues {
  return {
    name: record.name,
    slug: record.slug,
    organization_type: record.organization_type ?? '',
    description: record.description ?? '',
    website: record.website ?? '',
    geographic_scope: record.geographic_scope ?? '',
    geographic_states: record.geographic_states ?? [],
    scope_description: record.scope_description ?? '',
    contact_name: contactPart(record.primary_contact, 'name'),
    contact_title: contactPart(record.primary_contact, 'title'),
    contact_email: contactPart(record.primary_contact, 'email'),
    contact_phone: contactPart(record.primary_contact, 'phone'),
    social_links: toSocialLinkRows(record.social_links),
    status: record.status,
  };
}

/**
 * Turn the form's flat shape into what the API expects.
 *
 * Anything blank is left out rather than sent as an empty string, and the
 * contact is omitted entirely when every part of it is blank - otherwise a new
 * account arrives holding a contact that exists and has no details, which is a
 * different claim from having no contact.
 */
export function toCreatePayload(values: OrganizationFormOutput): OrganizationCreate {
  const contact = contactToPayload({
    name: values.contact_name,
    title: values.contact_title,
    email: values.contact_email,
    phone: values.contact_phone,
  });

  const payload: OrganizationCreate = {
    name: values.name,
    status: values.status as OrganizationCreate['status'],
  };

  if (values.slug) payload.slug = values.slug;
  if (values.organization_type) {
    payload.organization_type =
      values.organization_type as OrganizationCreate['organization_type'];
  }
  if (values.description) payload.description = values.description;
  if (values.website) payload.website = values.website;
  if (values.geographic_scope) {
    payload.geographic_scope =
      values.geographic_scope as OrganizationCreate['geographic_scope'];
  }
  if (values.geographic_states.length)
    payload.geographic_states = values.geographic_states;
  if (values.scope_description) payload.scope_description = values.scope_description;
  // Left out entirely rather than sent as null: a new account with no contact
  // has never had one, and null is how an existing contact is cleared.
  if (contact) payload.primary_contact = contact;

  const links = toSocialLinksPayload(values.social_links);
  if (Object.keys(links).length) payload.social_links = links;

  return payload;
}

/**
 * Turn an edited form into a partial update, carrying only what changed.
 *
 * Sending the whole form on every save cannot tell "left alone" from "cleared",
 * and would overwrite a colleague's edit to a field this screen does not even
 * show - billing, the logo - with whatever was on screen when the page loaded.
 *
 * Which fields changed cannot be read from the values alone: the schema turns a
 * blank optional field into undefined, so a field that was emptied looks exactly
 * like one that never held anything. That is what the dirty map is for.
 */
export function toUpdatePayload(
  values: OrganizationFormOutput,
  dirtyFields: OrganizationDirtyFields,
): OrganizationUpdate {
  const payload: OrganizationUpdate = {};

  /*
   * The backend refuses to clear either of these, and the edit form refuses a
   * blank one before anything is sent. The guard is kept anyway: this function
   * is the last thing between the form and the request, and a null here would
   * come back as a 422 that reads like a fault rather than a typo.
   */
  if (isFieldDirty(dirtyFields, 'name') && values.name) {
    payload.name = values.name;
  }
  if (isFieldDirty(dirtyFields, 'slug') && values.slug) {
    payload.slug = values.slug;
  }

  /*
   * Everything else that is text: what was typed, or null when the box was
   * emptied. Null is how the backend is told to clear a field, as against
   * leaving it alone by not mentioning it. An empty string is the only falsy
   * value any of these can hold, so `|| null` is exactly the rule wanted.
   */
  if (isFieldDirty(dirtyFields, 'organization_type')) {
    payload.organization_type =
      (values.organization_type as OrganizationUpdate['organization_type']) || null;
  }
  if (isFieldDirty(dirtyFields, 'description')) {
    payload.description = values.description || null;
  }
  if (isFieldDirty(dirtyFields, 'website')) {
    payload.website = values.website || null;
  }
  if (isFieldDirty(dirtyFields, 'geographic_scope')) {
    payload.geographic_scope =
      (values.geographic_scope as OrganizationUpdate['geographic_scope']) || null;
  }
  if (isFieldDirty(dirtyFields, 'scope_description')) {
    payload.scope_description = values.scope_description || null;
  }

  // A list is replaced whole. There is no way to add one state without saying
  // what the rest of the list is.
  if (isFieldDirty(dirtyFields, 'geographic_states')) {
    payload.geographic_states = values.geographic_states;
  }

  /*
   * The contact is stored as one thing, so it is replaced as one thing. Editing
   * only the phone number and sending only the phone number would drop the name
   * and the email address with it.
   */
  const contactTouched = CONTACT_FIELDS.some((field) => isFieldDirty(dirtyFields, field));
  if (contactTouched) {
    payload.primary_contact = contactToPayload({
      name: values.contact_name,
      title: values.contact_title,
      email: values.contact_email,
      phone: values.contact_phone,
    });
  }

  /*
   * Links are one JSON value on the record, so the whole set goes or none of it
   * does. Sending only the row that changed would leave a link somebody deleted
   * exactly where it was.
   */
  if (isFieldDirty(dirtyFields, 'social_links')) {
    payload.social_links = toSocialLinksPayload(values.social_links);
  }

  // The status is never sent from this form, whatever the schema still produces
  // for it. It is changed from the account card, with a reason recorded.

  return payload;
}

/**
 * True when a 409 is about the web handle rather than the name.
 *
 * The backend distinguishes the two only in its wording: "The slug 'x' is
 * already in use" against "Another organization is already named 'X'". The
 * test for this pins both, so rewording them is noticed here rather than by a
 * person seeing the wrong message in the wrong place.
 */
export function isHandleConflict(message: string): boolean {
  return message.toLowerCase().includes('slug');
}
